// Etsy research routing helpers: tab paths, the `?q=` query and the lookup
// that runs for it.
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useSearch } from '@tanstack/react-router'
import { etsyResearchKeys, type EtsyResearchUsage } from '../../api/etsy-research'
import { useT } from '../../i18n'
import { ETSY_RESEARCH_STRINGS } from '../../i18n/etsy-research'

export type ResearchScope = 'seller' | 'admin'

// Alura data, PEXIFY team only (Alura API Terms §5): the admin shell opens the
// tabs, the seller shell shows a team-only notice and calls nothing.
export const RESEARCH_OPEN: Record<ResearchScope, boolean> = { admin: true, seller: false }
export type ResearchTab = 'keyword' | 'shop' | 'reviews' | 'listing' | 'today'

export const RESEARCH_TABS: readonly ResearchTab[] = ['keyword', 'shop', 'reviews', 'listing', 'today']

export function researchPath(scope: ResearchScope, tab: ResearchTab): string {
  return `/${scope}/tools/etsy-research/${tab}`
}

export function useResearchStrings() {
  return useT(ETSY_RESEARCH_STRINGS)
}

/** `?q=4354726818` arrives as a number — the router JSON-parses search values. */
export function researchSearch(search: Record<string, unknown>): { q?: string } {
  const q = search.q
  if (typeof q === 'string') return { q }
  if (typeof q === 'number' && Number.isFinite(q)) return { q: String(q) }
  return {}
}

export function useRouteQuery(): string {
  const search = useSearch({ strict: false }) as { q?: unknown }
  return researchSearch(search).q?.trim() ?? ''
}

/** Runs a lookup for the URL's query; the answer also refreshes the usage count. */
export function useResearchLookup<T extends { usage: EtsyResearchUsage }>(
  tab: ResearchTab,
  query: string,
  run: (q: string) => Promise<T>,
) {
  const queryClient = useQueryClient()
  return useQuery({
    queryKey: ['etsy-research', tab, query.toLowerCase()],
    queryFn: async () => {
      try {
        const result = await run(query)
        queryClient.setQueryData(etsyResearchKeys.usage, result.usage)
        if (result.usage.credits_remaining !== null)
          queryClient.setQueryData(etsyResearchKeys.credits, result.usage.credits_remaining)
        // A new keyword or shop search adds rows to today's opportunities.
        void queryClient.invalidateQueries({ queryKey: etsyResearchKeys.opportunities })
        return result
      } catch (error) {
        void queryClient.invalidateQueries({ queryKey: etsyResearchKeys.usage })
        throw error
      }
    },
    enabled: query.length > 0,
    staleTime: Infinity,
    retry: false,
  })
}
